import { defineComponent, computed, PropType, toRefs, nextTick, ref } from '@vue/composition-api';
import { get, isEmpty } from 'lodash-es';
import {
  useCreativeAuditInfo,
  useAdgroupAuditInfo,
  useAllowlist,
  useCreativeIs,
  useCreativeToolsForm,
  useCreativeAdgroupIs,
  useMaterial,
  useStdAdgroupIs,
} from '@tt4b/ttam-hooks';
import { i18n } from './../../../i18n';
import {
  CampaignFormData,
  AdgroupFormData,
  REJECT_AD_CAN_FIX_BY_VIDEO_EDIT_CODE_LIST,
  RejectInfo,
  REJECT_TYPES,
  NormalCreative,
  FullNormalFormData,
  AcoFormData,
  ACTION_TYPE,
  VideoTaskList,
} from '@tt4b/ttam-enums';
import { focusHighlightShow } from '@tt4b/ttam-utils';
import AuditRejectAppeal from './audit-reject-appeal/index.vue';
import VoteForAudit from './vote-for-audit.vue';
import VideoEdit from '@/components/creative/precheck-nav-list/video-edit/index.vue';

// 是否可以定位到拒审表单项处
const getIsCanPositionReasonType = item => {
  const reasonTypes = REJECT_TYPES.filter(o => o.domId).map(o => o.reason_type);

  return reasonTypes.includes(item.reasonType);
};

/** 定位到指定DOM */
const handleFocusPosition = item => {
  const reason = REJECT_TYPES.find(o => o.reason_type === item.reasonType);
  focusHighlightShow(reason?.domId);
};

const name = 'ttam-audit-reject-side';
export default defineComponent({
  name,
  components: {
    VoteForAudit,
    AuditRejectAppeal,
    VideoEdit,
  },
  props: {
    type: {
      type: String,
      default: 'creative',
    },
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    creativeData: {
      type: Object as PropType<FullNormalFormData & AcoFormData>,
      default: () => ({}),
    },
    normalCreativeData: {
      type: Object as PropType<NormalCreative>,
      default: () => ({}),
    },
    rejectInfo: {
      type: Object as PropType<RejectInfo>,
      default: () => ({}),
    },
    createMode: {
      type: String,
      default: () => '',
    },
  },
  setup(props, { emit }) {
    const { createMode, rejectInfo, type, normalCreativeData, adgroupData, campaignData, creativeData } = toRefs(props);
    const isCreativeStep = computed(() => type.value === 'creative');
    const isAdgroupStep = computed(() => type.value === 'adgroup');
    const { isLiveAds } = useStdAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });
    const { isAllRejected, isShowAppeal } = isAdgroupStep.value
      ? useAdgroupAuditInfo({
          rejectInfo,
        })
      : useCreativeAuditInfo({
          rejectInfo,
          createMode,
        });
    const { isAco } = useCreativeAdgroupIs({
      createMode,
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });
    const { isPullTTPostNativeAd, isCarouselImage, isNative } = useCreativeIs({
      campaignFormData: campaignData,
      adgroupFormData: adgroupData,
      normalCreativeData,
      createMode,
    });
    const { isVideoUploadfrontPrecheckWhite } = useAllowlist();
    const rejectTitle = computed(() =>
      isCreativeStep.value ? i18n.t('module_reject_ad_content') : i18n.t('module_reject_adgroup_content'),
    );
    const rejectReasons = computed(() => rejectInfo.value?.reasons || []);

    // 是否展示拒审理由
    const isShowAuditReject = computed(() => rejectReasons.value.length > 0);

    /** 多图轮播拒审理由 */
    const carouselImageRejectReasonsSorted = computed(() => {
      if (!isCarouselImage.value) {
        return [];
      }

      const isEmptyObjectId = val => (typeof val === 'number' ? false : isEmpty(val));
      const hasRejectMaterialIdReasons = rejectReasons.value.filter(({ objectId }) => !isEmptyObjectId(objectId));

      return creativeData.value.imageInfo
        ?.map(({ materialIds }) => hasRejectMaterialIdReasons.find(reason => materialIds?.includes(reason.objectId)))
        .filter(item => !isEmptyObjectId(item));
    });

    /** 多图轮播拒审图片Id */
    const rejectImageIds = computed(() => {
      if (isEmpty(carouselImageRejectReasonsSorted.value)) {
        return [];
      }

      return carouselImageRejectReasonsSorted.value?.map(item => item?.objectId);
    });

    /**
     * 轮播图创意拒审理由列表根据原始数据做如下处理衍生：
     * 将所有图片的拒审理由找出并排序，然后将非图片拒审理由排在后面
     */
    const rejectReasonsList = computed(() => {
      if (!isCarouselImage.value) {
        return rejectReasons.value;
      }
      // 其他拒审理由
      const otherRejectReasons = rejectReasons.value.filter(reason => !rejectImageIds.value?.includes(reason.objectId));

      return carouselImageRejectReasonsSorted.value?.concat(otherRejectReasons);
    });

    /**
     * 获取多图轮播的title
     */
    const getIsCarouselImageReject = item => rejectImageIds.value?.includes(item.objectId);
    const getRejectIndex = objectId => {
      if (!creativeData.value.imageInfo) {
        return 0;
      }

      return creativeData.value.imageInfo.findIndex(image => image.materialIds?.includes(objectId)) + 1;
    };
    const getRejectImageIndexTitle = item => {
      const param = { imageIndex: getRejectIndex(item.objectId) };
      const titlePrev = i18n.t('module_carousel_image_creative_rejection_image_index', param);

      return `${titlePrev}-${item.title}`;
    };
    // 是否要展示VideoEdit
    const isShowVideoEdit = item => {
      if (!creativeData.value?.creativeId) {
        return false;
      }

      const isVideo = Boolean(get(creativeData.value, 'videoInfo.vid', ''));

      // 非原生拉视频+视频素材+开白+视频编辑器能修复此问题
      return (
        !isPullTTPostNativeAd.value &&
        isVideo &&
        isVideoUploadfrontPrecheckWhite.value &&
        REJECT_AD_CAN_FIX_BY_VIDEO_EDIT_CODE_LIST.includes(item?.disapprovalCode)
      );
    };

    /** 获取拒审理由的title */
    const getReasonTitle = item => {
      if (getIsCarouselImageReject(item.objectId)) {
        return getRejectImageIndexTitle(item);
      }

      return item.title;
    };

    const adgroupId = computed(() => adgroupData.value.adId ?? '');
    const creativeId = computed(() => creativeData.value.creativeId ?? '');
    const objectiveType = computed(() => adgroupData.value.objectiveType);
    const promotionFlowType = computed(() => adgroupData.value.promotionFlowType);
    function handleAppealComplete() {
      emit('appeal-complete');
    }

    const { addVideoMaterialWithVideoTasks, updateVideoMaterialWithVideoTasks, updateVideoMaterial } = useMaterial({
      isNative,
      campaignFormData: campaignData,
      adgroupFormData: adgroupData,
      creativeFormData: creativeData,
    });

    function onVideoEdit(params) {
      const { onCreateVideoEditorClick } = useCreativeToolsForm({
        isAco,
        isNative,
        isCreate: computed(() => createMode.value === ACTION_TYPE.CREATE),
        // 要用到的方法不依赖这个值
        creativeLeftCount: ref(0),
      });
      emit('video-edit');
      nextTick(() => {
        onCreateVideoEditorClick({
          ...params,
          vid: props.creativeData?.videoInfo?.vid,
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onStart: (data: VideoTaskList, isUpdateVideoStatus: boolean) => {
            // DEBUG
            console.log('[button] use optimize start', data);
            addVideoMaterialWithVideoTasks({
              taskList: data,
              isReplace: isUpdateVideoStatus,
            });
          },
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onSuccess: (data: VideoTaskList) => {
            // DEBUG
            console.log('[button] use optimize success', data);

            if (!data.length) {
              return;
            }

            const hasTaskId = Boolean(data[0].task_id);

            if (hasTaskId) {
              updateVideoMaterialWithVideoTasks(data);
            } else {
              updateVideoMaterial(data);
            }
          },
        });
      });
    }
    const getReasonContent = item => {
      let str = item.reasons.join(',');
      str = str.split('<br>');
      return str;
    };
    return {
      name,
      isAllRejected,

      isCreativeStep,
      isAdgroupStep,
      rejectTitle,
      isShowAuditReject,
      isLiveAds,

      /** 是否可以定位到指定的DOM节点 */
      getIsCanPositionReasonType,
      /** 拒审理由列表 */
      rejectReasonsList,
      /** 获取拒审理由title */
      getReasonTitle,
      /** 定位到指定的DOM节点 */
      handleFocusPosition,
      /** 是否展示一键申述 */
      isShowAppeal,

      handleAppealComplete,
      isShowVideoEdit,

      adgroupId,
      creativeId,
      objectiveType,
      promotionFlowType,

      onVideoEdit,
      getReasonContent,
    };
  },
});
